import type { Schemas } from "@platform/contracts/client";
import { Button, Card, Label, Select, Textarea } from "@platform/ui";
import { ErrorText, errorKey, localName, useSession } from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslations } from "use-intl";
import { useStepUp } from "../components/step-up";
import { formatDateTime, locale } from "../lib/i18n";

type Message = Schemas["ContentMessage"];
type SupportMessage = Schemas["SupportMessage"];

const MIN_REASON = 10;
const REMOVAL_CATEGORIES = ["authority_direction", "online_harm_notice", "ip_notice", "other"] as const;
type RemovalCategory = (typeof REMOVAL_CATEGORIES)[number];
const CARD_TYPES = [
  "order",
  "inquiry",
  "quote",
  "shipment",
  "change_order",
  "product",
  "business_card",
] as const;
const kilobytes = new Intl.NumberFormat(locale === "zh" ? "zh-CN" : "en", {
  style: "unit",
  unit: "kilobyte",
  maximumFractionDigits: 0,
});

/**
 * One message's body as a reader sees it: text, a card's type, a file's name and size, or what it was. A recalled or
 * removed text or file also shows what it said to support, which the log keeps for support's reading alone.
 */
export function MessageBody({ message }: { message: Message }) {
  const t = useTranslations();
  const value = message.body;
  switch (value.kind) {
    case "text":
      return <p className="whitespace-pre-wrap">{value.text}</p>;
    case "card":
      return (
        <p className="text-ink-muted">
          {t("admin.content.card", {
            type: (CARD_TYPES as readonly string[]).includes(value.entityType)
              ? t(`admin.content.cardTypes.${value.entityType as (typeof CARD_TYPES)[number]}`)
              : value.entityType,
          })}
        </p>
      );
    case "file":
      return (
        <p className="text-ink-muted">
          {t("admin.content.file", { name: value.name, size: kilobytes.format(value.sizeBytes / 1024) })}
        </p>
      );
    case "recalled":
    case "removed":
      return (
        <>
          <p className="text-ink-muted">{t(`admin.content.${value.kind}`)}</p>
          {value.was !== undefined && (
            <div className="flex flex-col gap-1 border-l-2 border-border-subtle pl-3">
              <p className="text-caption text-ink-muted">{t("admin.content.was")}</p>
              <MessageBody message={{ ...message, body: value.was }} />
            </div>
          )}
        </>
      );
    default:
      return <p className="text-ink-muted">{t(`admin.content.${value.kind}`)}</p>;
  }
}

/** A person's text or file, recalled or not, is what Civentia may remove (card 146); cards and notices are not. */
const removable = (message: SupportMessage) =>
  message.sender.userId !== null && ["text", "file", "recalled"].includes(message.body.kind);

/**
 * Removing one message for a legal reason (card 126): why, the reason the sender's admins read, whether the law forbids
 * saying it, and a step-up.
 */
function RemoveForm({
  conversationId,
  messageId,
  onRemoved,
  onCancel,
}: {
  conversationId: string;
  messageId: string;
  onRemoved: () => void;
  onCancel: () => void;
}) {
  const t = useTranslations();
  const { session } = useSession();
  const stepUp = useStepUp();
  const [category, setCategory] = useState<RemovalCategory>("authority_direction");
  const [reason, setReason] = useState("");
  const [withhold, setWithhold] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const id = `remove-${messageId}`;

  const remove = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    const outcome = await stepUp.run(() =>
      session.api.POST("/v1/ops/conversations/{conversationId}/messages/{messageId}/remove", {
        params: { path: { conversationId, messageId } },
        body: { category, reason: reason.trim(), withholdReason: withhold },
      }),
    );
    if (outcome.error !== undefined) return setError(t(errorKey(outcome.error)));
    onRemoved();
  };

  return (
    <form
      className="flex flex-col gap-3 border-t border-border-subtle pt-3"
      onSubmit={(event) => void remove(event)}
    >
      <p className="font-medium text-ink">{t("admin.content.removeTitle")}</p>
      <p className="text-ink-muted">{t("admin.content.removeIntro")}</p>
      <Label htmlFor={`${id}-category`}>{t("admin.content.category")}</Label>
      <Select
        id={`${id}-category`}
        value={category}
        onChange={(event) => setCategory(event.target.value as RemovalCategory)}
      >
        {REMOVAL_CATEGORIES.map((each) => (
          <option key={each} value={each}>
            {t(`admin.content.categories.${each}`)}
          </option>
        ))}
      </Select>
      <Label htmlFor={`${id}-reason`}>{t("admin.content.removalReason")}</Label>
      <Textarea
        id={`${id}-reason`}
        value={reason}
        maxLength={2000}
        onChange={(event) => setReason(event.target.value)}
      />
      <label className="flex cursor-pointer items-center gap-2 text-ink">
        <input
          type="checkbox"
          className="size-4 accent-brand-600"
          checked={withhold}
          onChange={(event) => setWithhold(event.target.checked)}
        />
        {t("admin.content.withhold")}
      </label>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      <div className="flex gap-2">
        <Button type="submit" variant="danger" disabled={reason.trim().length < MIN_REASON}>
          {t("admin.content.confirmRemove")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t("admin.content.cancel")}
        </Button>
      </div>
      {stepUp.dialog}
    </form>
  );
}

/**
 * A conversation's messages for support (ADM-5, card 082), read from its log (card 146): only after a reason and a
 * step-up, for 30 minutes (card 081). Without a grant, or once it has ended, the page asks for one again. Opened from
 * an order's page, it names the senders' enterprises from the order. An operator who may remove content removes a
 * person's text or file from here, by its entry (card 126).
 */
export function ConversationMessagesPage() {
  const t = useTranslations();
  const { conversationId = "" } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("orderId") ?? undefined;
  const { session, state: signedIn } = useSession();
  const stepUp = useStepUp();
  const removes = signedIn.status === "signed_in" && signedIn.claims.perm.includes("ops:content.remove");
  const [messages, setMessages] = useState<readonly SupportMessage[]>([]);
  const [removing, setRemoving] = useState<string | undefined>();
  const [removed, setRemoved] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | undefined>();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [state, setState] = useState<"loading" | "open" | "closed" | "ended">("loading");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [order, setOrder] = useState<Schemas["OpsOrderView"] | undefined>();

  /** Reads a page: the first replaces what is shown; a later one adds earlier messages above it. */
  const read = useCallback(
    async (page: number) => {
      const { data, error: problem } = await session.api.GET(
        "/v1/ops/conversations/{conversationId}/messages",
        {
          params: { path: { conversationId }, query: { page } },
        },
      );
      if (data === undefined) {
        if ((problem as { code?: string } | undefined)?.code === "CONTENT_ACCESS_REQUIRED")
          return setState((current) => (current === "open" ? "ended" : "closed"));
        return setError(t(errorKey(problem)));
      }
      // The page comes newest first; the reader shows the oldest at the top.
      const older = [...data.items].reverse();
      setMessages((current) => (page === 1 ? older : [...older, ...current]));
      setExpiresAt(data.expiresAt);
      setNextPage(data.nextPage);
      setState("open");
    },
    [session, conversationId, t],
  );

  useEffect(() => {
    void read(1);
  }, [read]);

  useEffect(() => {
    if (orderId === undefined) return;
    void session.api
      .GET("/v1/ops/orders/{orderId}", { params: { path: { orderId } } })
      .then(({ data }) => setOrder(data));
  }, [session, orderId]);

  const ask = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    const outcome = await stepUp.run(() =>
      session.api.POST("/v1/ops/conversations/{conversationId}/content-access", {
        params: { path: { conversationId } },
        body: { reason: reason.trim() },
      }),
    );
    if (outcome.error !== undefined) return setError(t(errorKey(outcome.error)));
    setReason("");
    await read(1);
  };

  const enterpriseName = (enterpriseId: string | null) => {
    if (order === undefined || enterpriseId === null) return undefined;
    return localName(enterpriseId === order.buyer.enterpriseId ? order.buyer.name : order.shop.name, locale);
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-title font-semibold text-ink">
        {order === undefined
          ? t("admin.content.title")
          : t("admin.content.titleOf", { number: order.number })}
      </h1>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {(state === "closed" || state === "ended") && (
        <Card>
          <form className="flex flex-col gap-3" onSubmit={(event) => void ask(event)}>
            {state === "ended" && (
              <p className="text-small text-tone-problem-fg">{t("admin.content.ended")}</p>
            )}
            <p className="text-small text-ink-muted">{t("admin.content.askIntro")}</p>
            <Label htmlFor="content-reason">{t("admin.content.reason")}</Label>
            <Textarea
              id="content-reason"
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
            />
            <Button type="submit" className="self-start" disabled={reason.trim().length < MIN_REASON}>
              {t("admin.content.ask")}
            </Button>
          </form>
        </Card>
      )}
      {state === "open" && expiresAt !== undefined && (
        <>
          <p className="text-small text-ink-muted">
            {t("admin.content.endsAt", { when: formatDateTime(expiresAt) })}
          </p>
          {nextPage !== null && (
            <Button variant="secondary" className="self-start" onClick={() => void read(nextPage)}>
              {t("admin.content.earlier")}
            </Button>
          )}
          {removed && (
            <p role="status" className="text-small text-tone-done-fg">
              {t("admin.content.removedNow")}
            </p>
          )}
          {messages.length === 0 && <p className="text-small text-ink-muted">{t("admin.content.none")}</p>}
          <ol className="flex flex-col gap-3">
            {messages.map((message) => {
              const enterprise = enterpriseName(message.sender.enterpriseId);
              return (
                <li key={message.id}>
                  <Card className="flex flex-col gap-1 text-small">
                    <p className="text-caption text-ink-muted">
                      {[
                        message.sender.displayName === ""
                          ? t("admin.content.system")
                          : message.sender.displayName,
                        ...(enterprise === undefined ? [] : [enterprise]),
                        formatDateTime(message.sentAt),
                      ].join(" · ")}
                    </p>
                    <MessageBody message={message} />
                    {removes &&
                      removable(message) &&
                      (removing === message.id ? (
                        <RemoveForm
                          conversationId={conversationId}
                          messageId={message.id}
                          onCancel={() => setRemoving(undefined)}
                          onRemoved={() => {
                            setRemoving(undefined);
                            setRemoved(true);
                            void read(1);
                          }}
                        />
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="self-start"
                          onClick={() => {
                            setRemoved(false);
                            setRemoving(message.id);
                          }}
                        >
                          {t("admin.content.remove")}
                        </Button>
                      ))}
                  </Card>
                </li>
              );
            })}
          </ol>
        </>
      )}
      {stepUp.dialog}
    </div>
  );
}
